'use client';

import { useMemo, useState } from 'react';
import {
  CONSTANT_META,
  computeConstantDigits,
  formatConstant,
  MAX_DIGITS,
  type MathConstant,
} from '@/lib/browser-constant-digits';

async function copyText(value: string) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const CONSTANTS: MathConstant[] = ['pi', 'e', 'phi'];

export default function BrowserPiDigitsGenerator() {
  const [constant, setConstant] = useState<MathConstant>('pi');
  const [digitsInput, setDigitsInput] = useState('100');
  const [message, setMessage] = useState<string | null>(null);

  const decimals = useMemo(() => {
    const parsed = Number.parseInt(digitsInput, 10);
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [digitsInput]);

  const result = useMemo(() => {
    if (!Number.isFinite(decimals)) {
      return { ok: false as const, message: 'Enter how many digits you want.' };
    }
    return computeConstantDigits(constant, decimals);
  }, [constant, decimals]);

  const formatted = result.ok ? formatConstant(result) : '';
  const meta = CONSTANT_META[constant];

  async function handleCopy() {
    if (!result.ok) return;
    try {
      await copyText(formatted);
      setMessage('Digits copied to the clipboard.');
    } catch {
      setMessage('The browser could not copy the digits.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Digits of π, e, φ</span>
        <h2 className="tool-live-title">Pi digits generator</h2>
        <p className="tool-live-subtitle">
          Generate the first N digits of pi, Euler’s number, or the golden ratio with an
          arbitrary-precision algorithm that runs entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Constant</span>
              <select
                onChange={(event) => setConstant(event.target.value as MathConstant)}
                value={constant}
              >
                {CONSTANTS.map((key) => (
                  <option key={key} value={key}>
                    {CONSTANT_META[key].label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Digits after the decimal point (max {MAX_DIGITS})</span>
              <input
                inputMode="numeric"
                max={MAX_DIGITS}
                min={0}
                onChange={(event) => setDigitsInput(event.target.value)}
                placeholder="100"
                type="number"
                value={digitsInput}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!result.ok}
              onClick={handleCopy}
              type="button"
            >
              Copy digits
            </button>
          </div>
          {message ? <p className="status-message status-success">{message}</p> : null}
          <p className="tool-live-panel-note">
            {meta.blurb} The count is capped at {MAX_DIGITS} digits so the tab stays responsive.
          </p>
        </section>

        <section className="tool-live-preview">
          {!result.ok ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.message}
            </p>
          ) : (
            <>
              <div className="tool-live-field tool-live-field-full">
                <span>
                  {meta.symbol} to {result.decimals} decimal place
                  {result.decimals === 1 ? '' : 's'}
                </span>
                <textarea
                  readOnly
                  rows={10}
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.6,
                    wordBreak: 'break-all',
                    resize: 'vertical',
                  }}
                  value={formatted}
                />
              </div>
              <p className="tool-live-panel-note">
                Showing {formatted.replace('.', '').length} total digits. Nothing is sent to a
                server; the digits are computed on your device.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
